import { effect, inject, Injector, signal, untracked, WritableSignal } from '@angular/core';

interface LensOptions {
  injector?: Injector;
}

/**
 * Replace every field in a type with a signal of that field.
 *
 * The resulting type is always an object, even if the value is nullable.
 */
export type Signalify<T> = {
  [Property in keyof NonNullable<T>]-?: WritableSignal<NonNullable<T>[Property]>;
};

/**
 * Create a lens for a given key in an object stored in a signal.
 *
 * When the outer signal is modified, the lens’ value is also updated.
 * When the inner signal is modified, the outer signal’s value is updated to contain the new value.
 *
 * @param model A writable signal, whose property we would like to extract.
 * @param key The key of the property to extract in the model.
 * @param options The options for initialising the necessary effects.
 *
 * @template T The overall shape of the model.
 * @template K A string literal type equal to the key.
 *
 * @returns a writable signal containing the value of this key.
 *
 * @example
 * const data = signal({ z: 1 });
 * const lens = makeLens(model, 'z'); // lens() = 1
 * data.set({ z: 2 }); // lens() = 2
 * lens.set(3); // data() = { z: 3 }
 */
function makeLens<T, K extends keyof T>(
  model: WritableSignal<T>,
  key: K,
  options: LensOptions,
): WritableSignal<NonNullable<T>[K]> {
  type Value = NonNullable<T>[K];
  const injector = options.injector ?? inject(Injector);
  const lens = signal<NonNullable<T>[K]>(model()?.[key] as Value);
  untracked(() => {
    effect(
      () => {
        const value = lens();
        untracked(() => model.update((existing) => ({ ...existing, [key]: value })));
      },
      { injector },
    );

    effect(
      () => {
        const value = model()?.[key] as Value;
        untracked(() => lens.set(value));
      },
      { injector },
    );
  });

  return lens;
}

/**
 * Wrap an object using lenses, so each field is replaced by a WritableSignal.
 * This is lazy: the lenses are only created when they are first accessed.
 *
 * @param model A writable signal, whose properties we would like to wrap in signals.
 * @param [options] The options for initialising the necessary effects.
 *
 * @example in the component
 * readonly object = model.required<MyType>();
 * protected readonly objectProxy = lensProxy(this.object);
 *
 * @example in the template
 * <input [(ngModel)]="objectProxy.myField" />
 */
export function lensProxy<T>(model: WritableSignal<T>, options: LensOptions = {}): NonNullable<Signalify<T>> {
  const injector = options.injector ?? inject(Injector);
  return new Proxy<Partial<Signalify<T>>>(
    {},
    {
      get(target, prop) {
        const key = prop as keyof T;
        target[key] ??= makeLens(model, key, { injector });
        return target[key];
      },
    },
  ) as Signalify<T>;
}
